import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';
import { lookupsJson, FarmRiskType } from '@ourbranch/lookups';

import Field from 'core/components/form/form.v2';
import Divider from 'core/components/divider';
import Label from 'core/components/label/label';

import useStyles from './exclusions.styles';

const RoofExclusionSection = ({ fieldName, disabled, riskType }) => {
  const classes = useStyles();
  const { values, initialValues, setFieldValue } = useFormikContext();

  const initialRoofLossSettlement = getIn(initialValues, `${fieldName}.roofLossSettlement`);
  const roofExclusion = getIn(values, `${fieldName}.roofExclusion`);
  const [isSectionOpen, setIsSectionOpen] = useState(roofExclusion && roofExclusion !== 'none');

  // For FO-1, only show "dwellingExcluded" option (NONE) since FO-1 is ACV-only.
  // "dwellingExcludedACV" doesn't make sense for FO-1 as it's already ACV-only by default.
  const roofExclusionOptions = useMemo(() => {
    const allOptions = lookupsJson.farm.roofExclusion;
    if (riskType === FarmRiskType.Fo1) {
      const filtered = allOptions.filter((opt) => opt.id === 'dwellingExcluded');
      if (filtered.length === 0) {
        console.error('roofExclusion lookup missing "dwellingExcluded" option for FO-1');
      }
      return filtered;
    }
    return allOptions;
  }, [riskType]);

  return (
    <Grid item xs={12}>
      <div className={classes.container}>
        <Grid container>
          <Field
            xs={12}
            type="switch"
            id={`${fieldName}.roofExclusionEnabled`}
            name={`${fieldName}.roofExclusionEnabled`}
            mode="dark"
            label="Dwelling and/or detached structures roof restriction"
            disabled={disabled}
            value={isSectionOpen}
            onChange={(_e, checked) => {
              setIsSectionOpen(checked);
              if (checked) {
                if (roofExclusion && roofExclusion !== 'none') {
                  return;
                }

                // For FO-1, default to "dwellingExcluded" (NONE) since it's the only option
                // For other risk types, default to "Actual Cash Value" option
                const defaultOption =
                  riskType === FarmRiskType.Fo1
                    ? roofExclusionOptions.find((opt) => opt.id === 'dwellingExcluded')
                    : roofExclusionOptions.find((opt) => opt.id === 'dwellingExcludedACV');

                if (defaultOption) {
                  setFieldValue(`${fieldName}.roofExclusion`, defaultOption.id);
                } else {
                  console.error('Default roof exclusion option not found', { riskType });
                }
              } else {
                setFieldValue(`${fieldName}.roofLossSettlement`, initialRoofLossSettlement);

                setFieldValue(`${fieldName}.roofExclusion`, 'none');
                setFieldValue(`${fieldName}.roofExclusionDetails`, undefined);
              }
            }}
          />
        </Grid>
        {isSectionOpen && (
          <>
            <Divider className={classes.divider} />
            <Label className={classes.label}>Dwelling and/or detached structures roof restriction</Label>
            <Grid container spacing={2}>
              <Field
                xs={12}
                type="select"
                id={`${fieldName}.roofExclusion`}
                name={`${fieldName}.roofExclusion`}
                mode="dark"
                label="Roof Exclusion Type"
                options={roofExclusionOptions}
                disabled={disabled}
              />
              <Field
                xs={12}
                type="textarea"
                id={`${fieldName}.roofExclusionDetails`}
                name={`${fieldName}.roofExclusionDetails`}
                mode="dark"
                label="Description of reason for for excluding the selected items (required)"
                rows={3}
                disabled={disabled}
              />
            </Grid>
          </>
        )}
      </div>
    </Grid>
  );
};

RoofExclusionSection.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  riskType: PropTypes.string.isRequired
};

RoofExclusionSection.defaultProps = {
  disabled: false
};

export default observer(RoofExclusionSection);
